import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import {
    Col, Collapse, message, Row,
} from 'antd';
import { useFormState } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';

import Button from '~/components/Button';
import CheckinRecognitionTable from '~/components/CheckinRecognitionTable';
import Form from '~/components/Form';
import Input from '~/components/Input';
import InputSearch from '~/components/InputSearch';
import Modal from '~/components/Modal';
import SpinnerOverlay from '~/components/SpinnerOverlay';
import { countryOption } from '~/helpers/country-helper';
import { defaultExpirationDate, serializeExpirationDate } from '~/helpers/date-helper';
import { wrapDialog } from '~/helpers/dialog-helper';
import { extractRequestError } from '~/helpers/error-helper';
import { fileToBase64 } from '~/helpers/file-helper';
import { isForeignerToggle, resolveForeigner } from '~/helpers/foreigner-helper';
import { wrapForm } from '~/helpers/form-helper';
import {
    maskCpfCnpj, maskTelefone, removeMaskAndLetters, removeMaskCNPJ, removeMaskE164,
} from '~/helpers/mask-helper';
import {
    validateCPFCNPJ, validateNotEmpty, validateOptionalEmail, validateOptionalPhone,
} from '~/helpers/validations';
import useAsync from '~/hooks/use-async';
import useAxios from '~/hooks/use-axios';
import useDidMount from '~/hooks/use-did-mount';
import useDidMountAndUpdate from '~/hooks/use-did-mount-and-update';
import { DOCCUMENT_TYPES } from '~/values/enums';

import CompanySelect from '../people/CompanySelect';
import ModalValidatePhoto from '../people/ModalValidatePhoto';
import PersonPicture from '../people/PersonPicture';
import WebcamPicture from '../people/WebcamPicture';
import {
    clearedFieldsOnToggle, detectForeigner, resolveDocumentType, saveBlockers,
} from './checkinDocument.helpers';
import styles from './CheckinDocumentModal.module.scss';

const { Panel } = Collapse;

const INITIAL_DOCUMENT_TYPE = {
    key: 0,
    label: 'CPF / CNPJ',
    item: {
        key: 0,
        label: 'CPF / CNPJ',
        name: 'documentCPF',
    },
};

const EXPIRATION_SHOW_TIME = { format: 'HH:mm' };

const getDocumentFieldName = label => {
    if (label === 'CPF / CNPJ') return 'documentCPF';
    if (label === 'RG') return 'documentRG';
    return 'documentOther';
};

const CheckinDocumentModal = ({
    history,
    onCancel, handleSubmit, form,
    ...others
}) => {
    const [photo, setPhoto] = useState(null);
    const [recognizedData, setRecognizedData] = useState([]);
    const [photoValidated, setPhotoValidated] = useState(false);
    const [loadingPhotoValidation, setLoadingPhotoValidation] = useState(false);
    const [step, setStep] = useState('search'); // search, form
    const [initialPhotoFile, setInitialPhotoFile] = useState(null);
    const [modalWidth, setModalWidth] = useState(700);
    const [checkinAssociationType, setcheckinAssociationType] = useState(0);
    const { values: formValues } = useFormState();
    const shouldRedirectRef = useRef(false);
    const modalAlreadyShownRef = useRef(false);

    const api = useAxios();

    useDidMount(() => {
        api.get('/GeneralSettings/GetListingGeneralSettings')
            .then(({ data }) => setcheckinAssociationType(data.checkinAssociationType))
            .catch(err => extractRequestError(
                err, 'Não foi possível obter as configurações. Por favor, recarregue a página.',
            ));

        form.initialize({ documentType: INITIAL_DOCUMENT_TYPE });
    });

    useEffect(() => {
        setModalWidth(step === 'form' ? 900 : 700);
    }, [step]);

    const hasAnyDocument = !!formValues.documentCPF || !!formValues.documentRG || !!formValues.documentOther;
    const hasCompany = !!formValues.companyId;
    const hasHost = !!formValues.host;
    const hasGroup = !!formValues.personGroupId?.item;
    const hasPhoto = formValues.images?.length > 0;
    const hasName = !!formValues.name?.trim();
    const isHost = !!formValues.isHost;
    const group = formValues.personGroupId?.item;

    const hostSearchParams = useMemo(
        () => ({ excludePersonId: formValues?.id }),
        [formValues?.id],
    );

    // Memoizado porque entra nas dependências do useCallback que renderiza o rodapé:
    // um array novo a cada render recriaria o callback sem necessidade.
    const blockers = useMemo(() => saveBlockers({
        hasName, hasAnyDocument, hasGroup, hasPhoto, hasHost, hasCompany, isHost, group,
    }), [hasName, hasAnyDocument, hasGroup, hasPhoto, hasHost, hasCompany, isHost, group]);
    const disableSave = blockers.length > 0;

    const validatePicture = useCallback(async pictureBase64 => {
        try {
            const { data } = await api.post('/PersonPicture/ValidatePersonPicture', { pictureBase64 });

            if (!data.value.isValidated) {
                if (!modalAlreadyShownRef.current) {
                    modalAlreadyShownRef.current = true;
                    ModalValidatePhoto.show({
                        rejectionReason: data.value.pictureRejectionReason,
                    });
                }

                setPhotoValidated({
                    valid: false,
                    rejectionReason: data.value.pictureRejectionReason,
                });
                return false;
            }

            modalAlreadyShownRef.current = false;
            setPhotoValidated({
                valid: true,
                rejectionReason: null,
            });

            return true;
        } catch (err) {

            extractRequestError(
                err,
                'Não foi possível validar a foto. Por favor, tente novamente.',
            );
            return false;
        }
    }, [api]);

    const updatePhoto = useCallback(async (personId, pictureBase64) => {
        try {
            const { data: pictureData } = await api.post('/PersonPicture/UpdatePersonFace', [{
                personId,
                pictureBase64,
            }]);

            if (!pictureData || !pictureData.value.isValidated) {
                setPhotoValidated({
                    valid: false,
                    rejectionReason: pictureData.value.pictureRejectionReason,
                });
                ModalValidatePhoto.show({
                    rejectionReason: pictureData.value.pictureRejectionReason,
                });
                return false;
            }

            setPhotoValidated({
                valid: true,
                rejectionReason: null,
            });

            return true;
        } catch (err) {
            extractRequestError(
                err,
                'Não foi possível atualizar a foto do usuário. Por favor, tente novamente.',
            );
            return false;
        }
    }, [api]);

    useEffect(() => {
        const validateImageIfNeeded = async () => {
            const image = form.getState().values.images?.[0];
            if (step === 'form' && image && photoValidated === false) {
                setLoadingPhotoValidation(true);
                const pictureBase64 = await fileToBase64(image);
                await validatePicture(pictureBase64);
                setLoadingPhotoValidation(false);
            }
        };
        validateImageIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const { call: handleSavePermission, loading: loadingSubmit } = useAsync(async values => {
        try {
            form.registerField('images', () => {}, { value: true });
            const shouldRedirect = shouldRedirectRef.current;

            const image = form.getState().values.images?.[0];

            const pictureBase64 = image
                ? await fileToBase64(image)
                : null;

            const payload = [{
                id: values.id ?? 0,
                name: values.name,
                description: values.description ?? '',
                documentCPF: values.documentCPF ?? null,
                documentRG: values.documentRG ?? null,
                documentType: resolveDocumentType(values),
                // Desde AD-008 a flag e persistida em Person.IsForeigner, em vez de
                // ser apenas derivada em tela pelo detectForeigner.
                isForeigner: values.isForeigner ?? false,
                documentOther: values.documentOther ?? null,
                email: values.email ?? '',
                phoneNumber: values.phoneNumber ?? '',
                birthDate: values.birthDate ?? null,
                // Campo proprio, e nao expirationDate: o totem manda "agora" em expirationDate e o
                // backend o ignora. Vazio, vale o padrao do grupo (AD-014).
                checkinExpirationDate: serializeExpirationDate(values.expirationDate),
                isHost: values.isHost ?? false,
                personGroupId: values.personGroupId?.item?.id,
                companyHostId: values.companyId ?? null,
                hostId: values.host?.item?.personId ?? null,
                // O seletor guarda { key, label, item }; o antigo item.code nao existia e o
                // pais escolhido ia como null.
                passportCountryCode: values.passportCountryCode?.key ?? null,
                pictureBase64,
                hostName: '',
                nickname: '',
                hostPhoneNumber: '',
            }];

            const documentValue = values.documentOther || values.documentRG || values.documentCPF;

            await api.post('/Person/IncludeUpdatePersonFromCheckin', payload);

            const { data } = await api.get(`/Person/GetByDocumentNumber/${documentValue}`);

            const isNewPerson = !(values.id && values.id !== 0);
            const photoWasChanged = image && image !== initialPhotoFile;
            const shouldUpdatePhoto = image && (isNewPerson || photoWasChanged);

            if (shouldUpdatePhoto) {
                await updatePhoto(data.id, pictureBase64);
            }

            message.success('Cadastro salvo com sucesso!');

            if (shouldRedirect && data.id) {
                window.location.href = `/app/people/${data.id}`;
            } else {
                // Se o cadastro desta pessoa já está aberto por baixo do modal,
                // ele não reflete as alterações feitas no check-in (ex.: anfitrião).
                // Recarrega apenas nesse caso para trazer os dados atualizados.
                const isViewingThisPerson = data.id
                    && window.location.pathname.endsWith(`/people/${data.id}`);
                onCancel();
                if (isViewingThisPerson) {
                    window.location.reload();
                }
            }

        } catch (err) {
            extractRequestError(err, 'Erro ao salvar o cadastro.');
        }
    }, [api, form, onCancel, updatePhoto]);

    const handleSaveAndRedirect = useCallback(() => {
        shouldRedirectRef.current = true;
        handleSubmit(handleSavePermission)();
    }, [handleSavePermission, handleSubmit]);

    const renderInputs = useCallback(() => {
        const {
            documentOther, images, isForeigner,
        } = form.getState().values;

        return (
            <>
                <h2>Novo Check-in</h2>
                <div className={styles.infoContainer}>
                    <OnChange name="images">
                        {(value, previous) => {
                            if (!previous || value === previous) return;
                            setPhotoValidated(false);
                            setLoadingPhotoValidation(true);

                            if (value.length) {
                                fileToBase64(value[0])
                                    .then(pictureBase64 => validatePicture(pictureBase64))
                                    .finally(() => setLoadingPhotoValidation(false));
                            } else {
                                setLoadingPhotoValidation(false);
                            }
                        }}
                    </OnChange>
                    <div className={styles.inputs}>
                        <OnChange name="isForeigner">
                            {(value, previous) => {
                                if (!isForeignerToggle(value, previous)) return;
                                clearedFieldsOnToggle(!!value)
                                    .forEach(field => form.change(field, undefined));
                            }}
                        </OnChange>
                        <Input.Field
                            type="switch"
                            name="isForeigner"
                            labelType="default"
                            label="Cliente estrangeiro"
                        />
                        {isForeigner ? (
                            <Row gutter={8}>
                                <Col flex={1}>
                                    <Input.Field
                                        name="documentOther"
                                        label="Outro Documento"
                                        maxLength={9}
                                    />
                                </Col>
                                <Col flex={15}>
                                    <InputSearch.Field
                                        name="passportCountryCode"
                                        type="select"
                                        allowClear
                                        required={documentOther}
                                        searchType="countries"
                                        validate={validateNotEmpty}
                                        placeholder="Selecione o país"
                                        label="País"
                                    />
                                </Col>
                            </Row>
                        ) : (
                            <Row gutter={8}>
                                <Col flex={2}>
                                    <Input.Field
                                        name="documentCPF"
                                        label="CPF / CNPJ"
                                        maxLength={18}
                                        validate={validateCPFCNPJ}
                                        format={maskCpfCnpj}
                                        parse={removeMaskCNPJ}
                                    />
                                </Col>
                                <Col flex={2}>
                                    <Input.Field
                                        name="documentRG"
                                        label="RG"
                                        maxLength={11}
                                    />
                                </Col>
                            </Row>
                        )}
                        <Input.Field
                            name="name"
                            label="Nome"
                            required
                            validate={validateNotEmpty}
                        />
                        <Input.Field
                            name="description"
                            label="Descrição"
                        />
                        <InputSearch.Field
                            name="personGroupId"
                            searchType="groups"
                            label="Grupo"
                            required
                            validate={validateNotEmpty}
                        />
                        <OnChange name="personGroupId">
                            {(value, previous) => {
                                if (value === previous) return;
                                form.change('expirationDate', defaultExpirationDate(value?.item?.defaultDaysToExpire));
                            }}
                        </OnChange>
                        <Input.Field
                            type="date"
                            name="expirationDate"
                            showTime={EXPIRATION_SHOW_TIME}
                            dateFormat="DD/MM/YYYY HH:mm"
                            label="Válido até"
                        />

                        {checkinAssociationType === 0 ? (
                            <InputSearch.Field
                                name="host"
                                searchType="hosts"
                                label="Anfitrião"
                                params={hostSearchParams}
                            />
                        ) : checkinAssociationType === 1 ? (
                            <CompanySelect.Field
                                name="companyId"
                                label="Empresa"
                            />
                        ) : (
                            <>
                                <CompanySelect.Field
                                    name="companyId"
                                    label="Empresa"
                                />
                                <InputSearch.Field
                                    name="host"
                                    searchType="hosts"
                                    label="Anfitrião"
                                    params={hostSearchParams}
                                />
                            </>
                        )}

                        {/* Mesmo pareamento parse/validate da PersonPage: com a flag
                            ligada o validate exige "+", entao o parse nao pode ser o
                            removeMaskAndLetters, que apaga o "+" e tornaria o campo
                            insatisfazivel. */}
                        <Input.Field
                            name="phoneNumber"
                            label={isForeigner ? 'Telefone (formato internacional)' : 'Telefone'}
                            placeholder={isForeigner ? '+5511987654321' : undefined}
                            maxLength={isForeigner ? 16 : undefined}
                            validate={validateOptionalPhone}
                            format={isForeigner ? undefined : maskTelefone}
                            parse={isForeigner ? removeMaskE164 : removeMaskAndLetters}
                        />
                        <Input.Field
                            name="email"
                            label="E-mail"
                            maxLength={320}
                            validate={validateOptionalEmail}
                        />

                        <Collapse className={styles.collapse}>
                            <Panel key="1" header="Mais informações">
                                {!isForeigner && (
                                    <Row gutter={8}>
                                        <Col flex={1}>
                                            <Input.Field
                                                name="documentOther"
                                                label="Outro Documento"
                                                maxLength={9}
                                            />
                                        </Col>
                                        <Col flex={15}>
                                            <InputSearch.Field
                                                name="passportCountryCode"
                                                type="select"
                                                allowClear
                                                required={documentOther}
                                                searchType="countries"
                                                validate={validateNotEmpty}
                                                placeholder="Selecione o país"
                                                label="País"
                                            />
                                        </Col>
                                    </Row>
                                )}
                                <Input.Field
                                    type="date"
                                    name="birthDate"
                                    dateFormat="DD/MM/YYYY"
                                    label="Data de nascimento"
                                />
                                <Input.Field
                                    type="date"
                                    name="creationDate"
                                    dateFormat="DD/MM/YYYY HH:mm:ss"
                                    disabled
                                    label="Data de criação"
                                />
                                <Input.Field
                                    type="switch"
                                    name="isHost"
                                    labelType="default"
                                    label="Anfitrião"
                                />
                            </Panel>
                        </Collapse>
                    </div>
                    <PersonPicture
                        form={form}
                        images={images}
                        photoValidated={photoValidated}
                        isLoadingValidation={loadingPhotoValidation}
                    />

                </div>
                <Row>
                    <Col>
                        {/* O botão nunca fica cinza sem dizer por quê: era assim que o
                            bloqueio empurrava as pessoas a ligarem o toggle "Anfitrião"
                            só para conseguir salvar (AD-011). */}
                        {disableSave ? (
                            <div className={styles.saveBlockers}>
                                {`Para salvar, falta: ${blockers.join(', ')}.`}
                            </div>
                        ) : null}
                        <div className={styles.submitAction}>
                            <Button
                                backgroundColor="#FFF"
                                fontColor="#000"
                                className={styles.cancelButton}
                                onClick={onCancel}
                            >
                                Cancelar
                            </Button>
                            <Button
                                backgroundColor="#FFF"
                                fontColor="#000"
                                className={styles.cancelButton}
                                disabled={disableSave}
                                onClick={handleSaveAndRedirect}
                                loading={loadingSubmit}
                            >
                                Salvar e abrir cadastro
                            </Button>
                            <Button
                                size="large"
                                type="primary"
                                htmlType="submit"
                                loading={loadingSubmit}
                                onClick={handleSubmit(handleSavePermission)}
                                disabled={disableSave}
                            >
                                Salvar
                            </Button>
                        </div>
                    </Col>
                </Row>
            </>
        );
    }, [form, checkinAssociationType, photoValidated, loadingPhotoValidation, onCancel,
        disableSave, handleSaveAndRedirect, loadingSubmit, handleSubmit, handleSavePermission, validatePicture,
        hostSearchParams, blockers]);

    const { documentType } = form.getState().values;

    const disableSubmit = form.getState().hasValidationErrors;

    const handleSelectRecognizedPerson = useCallback(async personId => {
        const { data } = await api.get(`/Person/GetPersonWithTWRules/${personId}`);

        const companyRequest = data.companyId && data.companyId !== 0
            ? api.get(`/Company/GetCompanyById/${data.companyId}`)
            : null;

        const [groupResponse, companyResponse] = await Promise.all([
            api.get(`/PersonGroup/GetByIdWithRules/${data.personGroupId}`),
            companyRequest,
        ]);

        const personGroup = groupResponse.data;
        const company = companyResponse?.data ?? null;

        const values = {
            id: data.id,
            name: data.name,
            documentCPF: data.documentCPF,
            documentRG: data.documentRG,
            documentOther: data.documentOther,
            isForeigner: resolveForeigner(data),
            email: data.email,
            phoneNumber: data.phoneNumber,
            birthDate: data.birthDate,
            creationDate: data.creationDate,
            isHost: data.isHost,
            description: data.description ?? '',
            personGroupId: {
                key: personGroup.id,
                label: personGroup.name,
                item: personGroup,
            },
            // Check-in renova: abre com o padrao do grupo, nunca com a validade antiga (AD-014).
            expirationDate: defaultExpirationDate(personGroup.defaultDaysToExpire),
        };

        if (company) {
            values.companyId = company.id;
            values.companyHostId = {
                key: company.id,
                label: company.name,
                item: company,
            };
        }

        if (data.host) {
            values.host = {
                key: data.host.id,
                label: data.host.name,
                item: { personId: data.host.id },
            };
        }

        if (data.passportCountryCode) {
            values.passportCountryCode = countryOption(data.passportCountryCode);
        }

        const person = recognizedData.find(x => x.personId === personId);

        let file;

        if (person.base64) {
            const response = await fetch(person.base64);
            const blob = await response.blob();
            file = new File([blob], 'profile.png', { type: blob.type });
        } else if (photo) {
            const blob = await fetch(photo).then(r => r.blob());
            file = new File([blob], 'profile.png', { type: 'image/png' });
        }

        if (file) {
            file.uuid = 'profile-image';
            file.url = URL.createObjectURL(file);
            values.images = [file];
            setInitialPhotoFile(file);
        }

        form.initialize(values);
        setStep('form');
    }, [api, form, photo, recognizedData]);

    const { call: onRecognize, loading: recognizing } = useAsync(async () => {
        try {
            const { data } = await api.post('/Person/GetMostSimilarPersonListingByPicture', { base64: photo });

            setRecognizedData(data.value);
        } catch (err) {
            extractRequestError(
                err,
                'Não foi possível reconhecer a foto. Por favor, tente novemante',
            );
        }
    }, [api, photo]);

    const { call: onSubmitDocument, loading: submitingDocument } = useAsync(async vals => {
        const documentFieldName = getDocumentFieldName(documentType?.label);
        const documentValue = vals[documentFieldName];

        try {
            const { data } = await api.get(`/Person/GetByDocumentNumber/${documentValue}`);

            const companyRequest = data.companyId && data.companyId !== 0
                ? await api.get(`/Company/GetCompanyById/${data.companyId}`)
                : null;

            const company = companyRequest?.data ?? null;

            const documentOther = data.documentOther
                ?? (documentType?.label === 'Outro Documento' ? documentValue : '');

            const values = {
                id: data.id,
                name: data.name,
                documentCPF: data.documentCPF ?? (documentType?.label === 'CPF / CNPJ' ? documentValue : ''),
                documentRG: data.documentRG ?? (documentType?.label === 'RG' ? documentValue : ''),
                documentOther,
                isForeigner: resolveForeigner(data, documentOther),
                email: data.email,
                phoneNumber: data.phoneNumber,
                birthDate: data.birthDate,
                creationDate: data.creationDate,
                isHost: data.isHost,
                description: data.description ?? '',
                personGroupId: {
                    key: data.personGroup.id,
                    label: data.personGroup.name,
                    item: data.personGroup,
                },
                expirationDate: defaultExpirationDate(data.personGroup.defaultDaysToExpire),
            };

            if (company) {
                values.companyId = company.id;
                values.companyHostId = {
                    key: company.id,
                    label: company.name,
                    item: company,
                };
            }

            if (data.hostId) {
                values.host = {
                    key: data.hostId,
                    label: data.hostName,
                    item: { personId: data.hostId },
                };
            }

            if (data.passportCountryCode) {
                values.passportCountryCode = countryOption(data.passportCountryCode);
            }

            let file;

            if (data.pictureBase64) {
                const response = await fetch(data.pictureBase64);
                const blob = await response.blob();
                file = new File([blob], 'profile.png', { type: blob.type });
            } else if (photo) {
                const blob = await fetch(photo).then(r => r.blob());
                file = new File([blob], 'profile.png', { type: 'image/png' });
            }

            if (file) {
                file.uuid = 'profile-image';
                file.url = URL.createObjectURL(file);
                values.images = [file];
                setInitialPhotoFile(file);
            }

            form.initialize(values);
            setStep('form');
        } catch (err) {
            message.warning('Pessoa não encontrada, cadastre uma nova.');

            const state = { [documentFieldName]: documentValue };
            state.isForeigner = detectForeigner(state);
            if (photo) {
                const blob = await fetch(photo).then(r => r.blob());
                const file = new File([blob], 'profile.png', { type: blob.type });
                file.uuid = 'profile-image';
                file.url = URL.createObjectURL(file);
                state.images = [file];
                setInitialPhotoFile(file);
            }
            form.initialize(state);
            setStep('form');
        }
    }, [api, documentType?.label, form, photo]);

    const handleRetryRecognition = useCallback(() => {
        setPhoto(null);
    }, []);

    useDidMountAndUpdate(() => {
        if (photo) {
            onRecognize();
        }
    }, [photo]);

    const renderPhotoRecognition = useCallback(() => {
        if (!photo) {
            return (
                <div className={styles.marginBottom16}>
                    <WebcamPicture
                        setPhoto={setPhoto}
                        label="Reconhecer"
                    />
                </div>
            );
        }

        if (recognizing) {
            return (
                <div className={styles.spinner}>
                    <SpinnerOverlay spinning tip="Reconhecendo..." />
                </div>
            );
        }

        if (!recognizedData.length) {
            return (
                <div className={styles.marginBottom16}>
                    <div className={styles.marginBottom16}>O reconhecimento falhou.</div>
                    <Button
                        onClick={handleRetryRecognition}
                    >
                        Reconhecer novamente
                    </Button>
                </div>
            );
        }

        return (
            <div className={styles.marginBottom16}>
                <CheckinRecognitionTable
                    dataSource={recognizedData}
                    onSelectPerson={handleSelectRecognizedPerson}
                />
            </div>
        );
    }, [photo, recognizing, recognizedData, handleSelectRecognizedPerson, handleRetryRecognition]);

    const renderDocumentInput = useCallback(() => {
        if (documentType?.label === 'CPF / CNPJ') {
            return (
                <Input.Field
                    name="documentCPF"
                    label="CPF / CNPJ"
                    autoFocus
                    validate={validateCPFCNPJ}
                    format={maskCpfCnpj}
                    parse={removeMaskCNPJ}
                    maxLength={18}
                    required
                />
            );
        }

        if (documentType?.label === 'RG') {
            return (
                <Input.Field
                    name="documentRG"
                    label="RG"
                    validate={validateNotEmpty}
                    maxLength={14}
                    required
                    autoFocus
                />
            );
        }

        return (
            <Input.Field
                name="documentOther"
                label="Outro Documento"
                validate={validateNotEmpty}
                maxLength={9}
                required
                autoFocus
            />
        );
    }, [documentType]);

    return (
        <Modal
            {...others}
            onCancel={onCancel}
            footer={null}
            width={modalWidth}
        >
            <Form onSubmit={handleSubmit(onSubmitDocument)}>
                {step === 'search' && (
                    <>
                        <h2>Reconheça por foto ou insira o documento</h2>
                        {renderPhotoRecognition()}
                        <Input.Field
                            required
                            type="select"
                            validate={validateNotEmpty}
                            name="documentType"
                            placeholder="Selecione o tipo"
                            label="Tipo do documento"
                            options={DOCCUMENT_TYPES}
                        />
                        {renderDocumentInput()}
                        <div className={styles.actions}>
                            <Button
                                htmlType="submit"
                                disabled={disableSubmit}
                                loading={submitingDocument}
                            >
                                Buscar
                            </Button>
                            <Button
                                onClick={onCancel}
                                type="primary"
                                backgroundColor="#FFF"
                                fontColor="#000"
                                className={styles.cancelButton}
                                loading={submitingDocument}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </>
                )}

                {step === 'form' && renderInputs()}
            </Form>
        </Modal>
    );
};

export default wrapDialog(wrapForm(CheckinDocumentModal));
